// @ts-strict-ignore
import { ColumnPicker } from "@dashboard/components/Datagrid/ColumnPicker/ColumnPicker";
import { useColumns } from "@dashboard/components/Datagrid/ColumnPicker/useColumns";
import Datagrid from "@dashboard/components/Datagrid/Datagrid";
import {
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { OrderLineFragment } from "@dashboard/graphql";
import { useIsMobile } from "@dashboard/hooks/useIsMobile";
import useListSettings from "@dashboard/hooks/useListSettings";
import { useOptionalOrderLineDiscountContext } from "@dashboard/products/components/OrderDiscountProviders/OrderLineDiscountProvider";
import { productPath } from "@dashboard/products/urls";
import { ListViews } from "@dashboard/types";
import { EditIcon, ExternalLinkIcon } from "@saleor/macaw-ui-next";
import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { messages as lineDiscountMessages } from "../OrderLineDiscountDialog/messages";
import { OrderLinesMobileList } from "../OrderLinesMobileList";
import { messages as orderMessages } from "../OrderListDatagrid/messages";
import { createGetCellContent, orderDetailsStaticColumnsAdapter } from "./datagrid";
import { messages } from "./messages";

interface OrderDetailsDatagridProps {
  lines: OrderLineFragment[];
  loading: boolean;
  onShowMetadata: (id: string) => void;
  enableVerticalBorder?: boolean;
  // Adds an "Edit price / discount" row action (needs OrderLineDiscountProvider).
  canEditLineDiscount?: boolean;
}

export const OrderDetailsDatagrid = ({
  lines,
  loading,
  onShowMetadata,
  enableVerticalBorder = true,
  canEditLineDiscount = false,
}: OrderDetailsDatagridProps) => {
  const intl = useIntl();
  const getLineDiscount = useOptionalOrderLineDiscountContext();
  const lineDiscountEnabled = canEditLineDiscount && !!getLineDiscount;
  const datagrid = useDatagridChangeState();
  const { updateListSettings, settings } = useListSettings(ListViews.ORDER_DETAILS_LIST);
  const orderDetailsStaticColumns = useMemo(() => orderDetailsStaticColumnsAdapter(intl), [intl]);
  const handleColumnChange = useCallback(
    picked => {
      if (updateListSettings) {
        updateListSettings("columns", picked.filter(Boolean));
      }
    },
    [updateListSettings],
  );
  const { handlers, visibleColumns, staticColumns, selectedColumns, recentlyAddedColumn } =
    useColumns({
      gridName: "order_details_products",
      staticColumns: orderDetailsStaticColumns,
      selectedColumns: settings?.columns ?? [],
      onSave: handleColumnChange,
    });
  const getCellContent = useCallback(
    createGetCellContent({
      columns: visibleColumns,
      data: lines,
      loading,
      onShowMetadata,
      intl,
    }),
    [intl, visibleColumns, loading],
  );
  const getMenuItems = useCallback(
    index => [
      {
        disabled: !lines[index]?.variant?.product.id,
        label: intl.formatMessage(messages.productDetails),
        Icon: lines[index]?.variant?.product.id ? (
          <Link to={productPath(lines[index].variant.product.id)} target="_blank">
            <ExternalLinkIcon />
          </Link>
        ) : (
          <ExternalLinkIcon />
        ),
        onSelect: () => false,
      },
      ...(lineDiscountEnabled && lines[index]
        ? [
            {
              label: intl.formatMessage(lineDiscountMessages.menuItem),
              Icon: <EditIcon />,
              onSelect: () => getLineDiscount(lines[index].id).openDialog(),
            },
          ]
        : []),
    ],
    [intl, lines, lineDiscountEnabled, getLineDiscount],
  );

  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <OrderLinesMobileList
        lines={lines}
        loading={loading}
        onShowMetadata={onShowMetadata}
        canEditLineDiscount={lineDiscountEnabled}
      />
    );
  }

  return (
    <DatagridChangeStateContext.Provider value={datagrid}>
      <Datagrid
        showEmptyDatagrid
        rowMarkers="none"
        columnSelect="single"
        freezeColumns={1}
        availableColumns={visibleColumns}
        verticalBorder={enableVerticalBorder}
        emptyText={intl.formatMessage(orderMessages.emptyText)}
        getCellContent={getCellContent}
        getCellError={() => false}
        menuItems={getMenuItems}
        rows={loading ? 1 : lines.length}
        selectionActions={() => null}
        onColumnResize={handlers.onResize}
        onColumnMoved={handlers.onMove}
        recentlyAddedColumn={recentlyAddedColumn}
        renderColumnPicker={() => (
          <ColumnPicker
            staticColumns={staticColumns}
            selectedColumns={selectedColumns}
            onToggle={handlers.onToggle}
          />
        )}
      />
    </DatagridChangeStateContext.Provider>
  );
};
